import { useTheme } from '@emotion/react'
import { css } from '@emotion/css'
import { useState, useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import { Form } from 'react-final-form'
import { FORM_ERROR } from 'final-form'
import styled from '@emotion/styled'
import _ from 'lodash'
import SSIChanges from '../onboarding/social-security-income/SSIChanges'
import { Page, Header } from './style'
import {
  Button,
  Spacer,
  Drawer,
  HelpIcon,
  TooltipText,
  FormError,
  Loading,
  ErrorPage,
} from '../../components'
import { numberToDollars, withRouterProps } from '../../utils'
import { API_URL } from '../../api'

const MarriedSSI = function (props) {
  const {
    navigate,
    location,
    store: {
      person,
      spouse,
      primarySSIBenefits,
      spouseSSIBenefits,
      getOptimizedSSI,
      primaryOptimizedSSI,
      spouseOptimizedSSI,
      saveSSBenefits,
      config,
    },
    store,
  } = props
  const [drawer, setDrawer] = useState('')
  const [page, setPage] = useState('default')
  const previousLocation = location.state?.location ?? null
  const theme = useTheme()

  useEffect(() => {
    props.main.setSidebar(false)
    props.main.setTopNav(false)

    document.documentElement.className = css`
      background: url(${API_URL + theme.bgRight}) no-repeat top right fixed;
      -webkit-background-size: cover;
      -moz-background-size: cover;
      -o-background-size: cover;
      background-size: cover;
    `
    return () => {
      document.documentElement.className = ''
      props.main.setSidebar(true)
      props.main.setTopNav(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRequest = async () => {
    try {
      setPage('loading')
      await getOptimizedSSI()
      setPage('optimizeSSI')
    } catch (err) {
      console.error(err)
      setPage('error')
      return {
        [FORM_ERROR]:
          'Something went wrong. Please check your information and try again. If the problem persists, please contact GuidedChoice.',
      }
    }
  }
  const handleSave = async () => {
    try {
      await saveSSBenefits({
        primarySSIBenefits: primaryOptimizedSSI,
        spouseSSIBenefits: spouseOptimizedSSI,
      })
      const isSpendown = _.get(config, 'isSpendown', null)
      if (isSpendown) {
        await store.processModifiedCase()
        return navigate('/spending', { state: { from: 'SSI' } })
      }
      return navigate('/overall')
    } catch (err) {
      console.error(err)
      return {
        [FORM_ERROR]:
          'Something went wrong. Please check your information and try again. If the problem persists, please contact GuidedChoice.',
      }
    }
  }

  const onSubmitDefaultPage = () => handleRequest()

  const onSubmitOptimizedPage = () => handleSave()

  if (page === 'loading') {
    return <Loading />
  }

  if (page === 'error') {
    return <ErrorPage />
  }

  if (page === 'default') {
    return (
      <>
        <Header />
        <Page
          css={css`
            max-width: 1000px;
          `}>
          <Page.Heading
            css={{
              maxWidth: '100%',
            }}
            title='Social Security income (SSI)'
          />
          <Page.PleaseNote>Please note: Social Security estimates are before-tax</Page.PleaseNote>
          <Spacer space='20px' />

          <Form
            onSubmit={onSubmitDefaultPage}
            subscription={{ submitting: true, submitError: true }}
            render={({ handleSubmit, submitting, submitError }) => (
              <>
                <FieldsGrid>
                  <Page.SSILabel />
                  <Page.SSIValue>{person.displayName}</Page.SSIValue>
                  <Page.SSIValue>{spouse.firstName}</Page.SSIValue>

                  <Page.SSILabel>Age SSI payments begin</Page.SSILabel>
                  <Page.SSIValue active>
                    {!primarySSIBenefits.includeSsi && 'N/A'}
                    {primarySSIBenefits.includeSsi &&
                      primarySSIBenefits.receivingSsi === 0 &&
                      primarySSIBenefits.paymentBeginAge}
                    {primarySSIBenefits.includeSsi &&
                      primarySSIBenefits.receivingSsi === 1 &&
                      'Already receiving'}
                  </Page.SSIValue>
                  <Page.SSIValue active>
                    {!spouseSSIBenefits.includeSsi && 'N/A'}
                    {spouseSSIBenefits.includeSsi &&
                      spouseSSIBenefits.receivingSsi === 0 &&
                      spouseSSIBenefits.paymentBeginAge}
                    {spouseSSIBenefits.includeSsi &&
                      spouseSSIBenefits.receivingSsi === 1 &&
                      'Already receiving'}
                  </Page.SSIValue>

                  <Page.SSILabel>Monthly amount</Page.SSILabel>
                  <Page.SSIValue active>
                    {primarySSIBenefits.includeSsi
                      ? `$${numberToDollars(primarySSIBenefits.monthlyAmount, true)}`
                      : 'N/A'}
                  </Page.SSIValue>
                  <Page.SSIValue active>
                    {spouseSSIBenefits.includeSsi
                      ? `$${numberToDollars(spouseSSIBenefits.monthlyAmount, true)}`
                      : 'N/A'}
                  </Page.SSIValue>

                  <Page.SSILabel>Status</Page.SSILabel>
                  <Page.SSIValue active>
                    {primarySSIBenefits.includeSsi ? 'Include' : 'Exclude'}
                  </Page.SSIValue>
                  <Page.SSIValue active>
                    {spouseSSIBenefits.includeSsi ? 'Include' : 'Exclude'}
                  </Page.SSIValue>

                  <Page.SSILabel>Use GuidedChoice estimate?</Page.SSILabel>
                  <Page.SSIValue active>
                    {primarySSIBenefits.includeSsi
                      ? primarySSIBenefits.useGcEstimate
                        ? 'Yes, estimate for me'
                        : "No, I'll enter my own"
                      : 'N/A'}
                  </Page.SSIValue>
                  <Page.SSIValue active>
                    {spouseSSIBenefits.includeSsi
                      ? spouseSSIBenefits.useGcEstimate
                        ? 'Yes, estimate for me'
                        : "No, I'll enter my own"
                      : 'N/A'}
                  </Page.SSIValue>
                </FieldsGrid>

                <>
                  <Page.SSIDivider />

                  <FieldsGrid>
                    <Page.SSILabel />
                    <Page.SSIValue>{person.displayName}</Page.SSIValue>
                    <Page.SSIValue>{spouse.firstName}</Page.SSIValue>

                    <Page.SSIHelpIconWrapper>
                      <Page.SSILabel>Full retirement age for SSI</Page.SSILabel>
                      <HelpIcon size='mediumLarge' tooltip={TooltipText.fullRetirementAge()} />
                    </Page.SSIHelpIconWrapper>
                    <Page.SSIValue> {primarySSIBenefits.fullRetirementAgeYear} </Page.SSIValue>
                    {person.includeSpouse && (
                      <Page.SSIValue>{spouseSSIBenefits.fullRetirementAgeYear}</Page.SSIValue>
                    )}

                    <Page.SSILabel>Monthly amount</Page.SSILabel>
                    <Page.SSIValue>
                      ${numberToDollars(primarySSIBenefits.fullRetirementAgeMonthlyAmount, true)}
                    </Page.SSIValue>
                    {person.includeSpouse && (
                      <Page.SSIValue>
                        ${numberToDollars(spouseSSIBenefits.fullRetirementAgeMonthlyAmount, true)}
                      </Page.SSIValue>
                    )}
                  </FieldsGrid>
                </>

                <Page.SSIDivider />

                <Page.YourChoice>It's your choice...</Page.YourChoice>
                {submitError && <FormError err={submitError} />}
                <Spacer space='20px' />

                <Page.SSIButtonContainer>
                  <Page.SSIOutlineButton
                    type='button'
                    label='Keep as is'
                    onClick={() =>
                      previousLocation
                        ? navigate(previousLocation.pathname, {
                            replace: true,
                            state: { from: 'SSI' },
                          })
                        : navigate(-1)
                    }
                    disabled={submitting}
                  />
                  <Page.SSISecondaryButton
                    type='button'
                    secondary
                    label='Make changes'
                    onClick={() => setDrawer('makeSSIChanges')}
                  />
                  {(primarySSIBenefits.includeSsi || spouseSSIBenefits.includeSsi) && (
                    <Page.SSIOptimizeButtonWrapper>
                      <Button
                        type='button'
                        primary
                        label='Optimize SSI'
                        onClick={handleSubmit}
                        disabled={submitting}
                      />
                      <Page.SSIHelpIconContainer>
                        <HelpIcon tooltip={TooltipText.optimizeSSI()} />
                      </Page.SSIHelpIconContainer>
                    </Page.SSIOptimizeButtonWrapper>
                  )}
                </Page.SSIButtonContainer>

                <Drawer
                  active={drawer === 'makeSSIChanges'}
                  title='Social Security Income (SSI)'
                  width='50%'>
                  <SSIChanges
                    includeSpouse={person.includeSpouse}
                    person={person}
                    spouse={spouse}
                    handleCancel={() => setDrawer('')}
                  />
                </Drawer>
              </>
            )}
          />
        </Page>
      </>
    )
  }

  if (page === 'optimizeSSI') {
    return (
      <>
        <Header />
        <Page
          css={css`
            max-width: 1000px;
          `}>
          <Page.Heading
            css={{
              maxWidth: '100%',
            }}
            title='Social Security income (SSI)'
          />
          <Page.PleaseNote>Please note: Social Security estimates are before-tax</Page.PleaseNote>
          <Spacer space='20px' />

          <Form
            onSubmit={onSubmitOptimizedPage}
            subscription={{ submitting: true, submitError: true }}
            render={({ handleSubmit, submitting, submitError }) => (
              <>
                <FieldsGrid>
                  <Page.SSILabel />
                  <Page.SSIValue>{person.displayName}</Page.SSIValue>
                  <Page.SSIValue>{spouse.firstName}</Page.SSIValue>

                  <Page.SSILabel>Currently selected age SSI begins</Page.SSILabel>
                  <Page.SSIValue active>
                    {!primarySSIBenefits.includeSsi && 'N/A'}
                    {primarySSIBenefits.includeSsi &&
                      primarySSIBenefits.receivingSsi === 0 &&
                      primarySSIBenefits.paymentBeginAge}
                    {primarySSIBenefits.includeSsi &&
                      primarySSIBenefits.receivingSsi === 1 &&
                      'Already receiving'}
                  </Page.SSIValue>
                  <Page.SSIValue active>
                    {!spouseSSIBenefits.includeSsi && 'N/A'}
                    {spouseSSIBenefits.includeSsi &&
                      spouseSSIBenefits.receivingSsi === 0 &&
                      spouseSSIBenefits.paymentBeginAge}
                    {spouseSSIBenefits.includeSsi &&
                      spouseSSIBenefits.receivingSsi === 1 &&
                      'Already receiving'}
                  </Page.SSIValue>

                  <Page.SSILabel>Monthly amount</Page.SSILabel>
                  <Page.SSIValue active>
                    {primarySSIBenefits.includeSsi
                      ? `$${numberToDollars(primarySSIBenefits.monthlyAmount, true)}`
                      : 'N/A'}
                  </Page.SSIValue>
                  <Page.SSIValue active>
                    {spouseSSIBenefits.includeSsi
                      ? `$${numberToDollars(spouseSSIBenefits.monthlyAmount, true)}`
                      : 'N/A'}
                  </Page.SSIValue>

                  <Page.SSILabel>Status</Page.SSILabel>
                  <Page.SSIValue active>
                    {primarySSIBenefits.includeSsi ? 'Include' : 'Exclude'}
                  </Page.SSIValue>
                  <Page.SSIValue active>
                    {spouseSSIBenefits.includeSsi ? 'Include' : 'Exclude'}
                  </Page.SSIValue>

                  <Page.SSILabel>Use GuidedChoice estimate?</Page.SSILabel>
                  <Page.SSIValue active>
                    {primarySSIBenefits.includeSsi
                      ? primarySSIBenefits.useGcEstimate
                        ? 'Yes, estimate for me'
                        : "No, I'll enter my own"
                      : 'N/A'}
                  </Page.SSIValue>
                  <Page.SSIValue active>
                    {spouseSSIBenefits.includeSsi
                      ? spouseSSIBenefits.useGcEstimate
                        ? 'Yes, estimate for me'
                        : "No, I'll enter my own"
                      : 'N/A'}
                  </Page.SSIValue>
                </FieldsGrid>

                <Page.SSIDivider />

                <Page.YourChoice>Optimized results</Page.YourChoice>

                <FieldsGrid>
                  <Page.SSILabel />
                  {primaryOptimizedSSI.includeSsi ? (
                    <Page.SSIValue>{person.displayName}</Page.SSIValue>
                  ) : (
                    <Page.SSIValue />
                  )}
                  {spouseOptimizedSSI.includeSsi ? (
                    <Page.SSIValue>{spouse.firstName}</Page.SSIValue>
                  ) : (
                    <Page.SSIValue />
                  )}

                  <Page.SSILabel>Optimal age to begin SSI</Page.SSILabel>
                  {primaryOptimizedSSI.includeSsi ? (
                    <Page.SSIValue>{_.get(primaryOptimizedSSI, 'paymentBeginAge')}</Page.SSIValue>
                  ) : (
                    <Page.SSIValue />
                  )}
                  {spouseOptimizedSSI.includeSsi ? (
                    <Page.SSIValue>{_.get(spouseOptimizedSSI, 'paymentBeginAge')}</Page.SSIValue>
                  ) : (
                    <Page.SSIValue />
                  )}

                  <Page.SSILabel>Monthly payment at optimal age</Page.SSILabel>
                  {primaryOptimizedSSI.includeSsi ? (
                    <Page.SSIValue>
                      ${numberToDollars(_.get(primaryOptimizedSSI, 'monthlyAmount', null), true)}
                    </Page.SSIValue>
                  ) : (
                    <Page.SSIValue />
                  )}
                  {spouseOptimizedSSI.includeSsi ? (
                    <Page.SSIValue>
                      ${numberToDollars(_.get(spouseOptimizedSSI, 'monthlyAmount', null), true)}
                    </Page.SSIValue>
                  ) : (
                    <Page.SSIValue />
                  )}
                </FieldsGrid>

                <Page.SSIDivider />

                <Page.YourChoice>It's your choice...</Page.YourChoice>
                {submitError && <FormError err={submitError} />}
                <Spacer space='20px' />

                <Page.SSIButtonContainer>
                  <Page.SSIOutlineButton
                    type='button'
                    ternary
                    label='Keep as is'
                    onClick={() =>
                      previousLocation
                        ? navigate(previousLocation.pathname, {
                            replace: true,
                            state: { from: 'SSI' },
                          })
                        : navigate(-1)
                    }
                    disabled={submitting}
                  />
                  <Page.SSISecondaryButton
                    type='button'
                    secondary
                    label='Make changes'
                    onClick={() => setDrawer('makeSSIChanges')}
                  />

                  <Button
                    type='button'
                    primary
                    label='Use Optimized SSI'
                    onClick={handleSubmit}
                    disabled={
                      primarySSIBenefits.paymentBeginAge === primaryOptimizedSSI.paymentBeginAge &&
                      parseInt(primarySSIBenefits.monthlyAmount) ===
                        parseInt(primaryOptimizedSSI.monthlyAmount) &&
                      spouseSSIBenefits.paymentBeginAge === spouseOptimizedSSI.paymentBeginAge &&
                      parseInt(spouseSSIBenefits.monthlyAmount) ===
                        parseInt(spouseOptimizedSSI.monthlyAmount)
                    }
                  />
                </Page.SSIButtonContainer>
                <Drawer
                  active={drawer === 'makeSSIChanges'}
                  title='Social Security Income (SSI)'
                  subtitle='Edit SSI'
                  width='50%'>
                  <SSIChanges
                    includeSpouse={person.includeSpouse}
                    person={person}
                    spouse={spouse}
                    handleCancel={() => setDrawer('')}
                  />
                </Drawer>
              </>
            )}
          />
        </Page>
      </>
    )
  }
  return null
}

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr 1fr;
  column-gap: 40px;
  row-gap: 10px;
  padding-left: 20px;
  align-items: center;
`

export default withRouterProps(inject('store', 'main')(observer(MarriedSSI)))
